import React from 'react';
import { ExternalLink, CheckCircle, AlertCircle, Info, Lightbulb } from 'lucide-react';

const EnhancedChatMarkdown = ({ content, className = '', isAssistant = false, citations = [], messageId = null, onCitationClick = null }) => {
  // 🔍 LOG DE DÉBOGAGE : Vérifier si les citations arrivent
  console.log('🔍 [EnhancedChatMarkdown] Props reçues:', {
    contentLength: content?.length || 0,
    citationsCount: citations?.length || 0,
    citations: citations,
    messageId: messageId,
    isAssistant
  });
  
  // Extraire le contenu s'il est dans un objet avec propriété 'reply'
  let actualContent = content;
  if (content && typeof content === 'object' && content.reply) {
    actualContent = content.reply;
  }
  
  // Vérifier que le contenu final est bien une string
  if (!actualContent || typeof actualContent !== 'string') {
    return <div className="text-gray-500 italic">Contenu vide</div>;
  }

  // Nettoyer le HTML existant et le convertir en markdown
  const cleanContent = (text) => {
    
    return text
      // Convertir les balises HTML en markdown
      .replace(/<h3>(.*?)<\/h3>/g, '## $1')
      .replace(/<h2>(.*?)<\/h2>/g, '## $1')
      .replace(/<h1>(.*?)<\/h1>/g, '# $1')
      .replace(/<strong>(.*?)<\/strong>/g, '**$1**')
      .replace(/<b>(.*?)<\/b>/g, '**$1**')
      .replace(/<em>(.*?)<\/em>/g, '*$1*')
      .replace(/<i>(.*?)<\/i>/g, '*$1*')
      
      // Convertir les listes
      .replace(/<ul>/g, '')
      .replace(/<\/ul>/g, '')
      .replace(/<li>(.*?)<\/li>/g, '- $1')
      .replace(/<ol>/g, '')
      .replace(/<\/ol>/g, '')
      
      // Nettoyer les balises restantes
      .replace(/<br\s*\/?>/g, '\n')
      .replace(/<p>/g, '')
      .replace(/<\/p>/g, '\n')
      .replace(/<div>/g, '')
      .replace(/<\/div>/g, '\n')
      
      // Nettoyer les espaces multiples et retours à la ligne
      .replace(/\n\s*\n/g, '\n\n')
      .trim();
  };

  // Fonction pour parser le contenu et retourner des éléments React
  const parseContent = (text) => {
    const cleanedText = cleanContent(text);
    
    const lines = cleanedText.split('\n');
    const elements = [];
    let elementKey = 0;

    lines.forEach((line, lineIndex) => {
      const trimmedLine = line.trim();
      
      // Ligne vide - petite séparation
      if (!trimmedLine) {
        elements.push(
          <div key={`space-${lineIndex}`} className="h-2" />
        );
        return;
      }

      // Traiter les titres markdown (## Titre) - Support niveaux 1-4
      const headingMatch = trimmedLine.match(/^(#{1,4})\s+(.+)$/);
      if (headingMatch) {
        const level = headingMatch[1].length;
        const text = headingMatch[2];
        const HeadingTag = `h${level}`;
        
        const headingClasses = {
          1: "text-xl font-bold text-gray-900 mb-3 mt-4 border-b border-gray-200 pb-2",
          2: "text-lg font-semibold text-gray-800 mb-2 mt-3",
          3: "text-base font-semibold text-gray-800 mb-2 mt-2",
          4: "text-sm font-semibold text-gray-700 mb-1 mt-2"
        };
        
        elements.push(
          React.createElement(
            HeadingTag,
            { 
              key: `heading-${lineIndex}`,
              className: headingClasses[level]
            },
            text
          )
        );
        return;
      }

      // Détecter les listes numérotées (1. Item)
      const numberedListMatch = trimmedLine.match(/^(\d+)\.\s+(.+)$/);
      if (numberedListMatch) {
        const number = numberedListMatch[1];
        const text = numberedListMatch[2];
        
        elements.push(
          <div key={`numbered-${lineIndex}`} className="flex items-start gap-3 mb-2 mt-2">
            <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-sm font-semibold">
              {number}
            </div>
            <div className="flex-1 text-gray-700">
              {processLineText(text, elementKey)}
            </div>
          </div>
        );
        return;
      }

      // Détecter les listes à puces (- Item)
      const bulletListMatch = trimmedLine.match(/^-\s+(.+)$/);
      if (bulletListMatch) {
        const text = bulletListMatch[1];
        
        elements.push(
          <div key={`bullet-${lineIndex}`} className="flex items-start gap-3 mb-2 mt-1">
            <div className="flex-shrink-0 w-2 h-2 bg-gray-400 rounded-full mt-2"></div>
            <div className="flex-1 text-gray-700">
              {processLineText(text, elementKey)}
            </div>
          </div>
        );
        return;
      }

      // Détecter les propositions en gras au début de ligne
      // Format: 1. **Titre** ou **1. Titre** ou juste **Titre**
      const propositionMatch1 = trimmedLine.match(/^(\d+)\.\s+\*\*([^*]+)\*\*(.*)$/);
      const propositionMatch2 = trimmedLine.match(/^\*\*(\d+\.\s[^*]+)\*\*(.*)$/);
      const propositionMatch3 = trimmedLine.match(/^\*\*([^*]+)\*\*(.*)$/);
      
      if (propositionMatch1) {
        // Format: 1. **Titre**
        const number = propositionMatch1[1];
        const titleText = propositionMatch1[2];
        const remainingText = propositionMatch1[3].trim();
        
        elements.push(
          <div key={`proposition-${lineIndex}`} className="flex items-start gap-3 mb-2 mt-2">
            <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-sm font-semibold">
              {number}
            </div>
            <div className="flex-1">
              <div className="font-semibold text-gray-900 mb-1">
                {titleText}
              </div>
              {remainingText && (
                <div className="text-gray-700 text-sm">
                  {processLineText(remainingText, elementKey)}
                </div>
              )}
            </div>
          </div>
        );
        return;
      } else if (propositionMatch2) {
        // Format: **1. Titre**
        const titleText = propositionMatch2[1];
        const remainingText = propositionMatch2[2].trim();
        
        elements.push(
          <div key={`proposition-${lineIndex}`} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 mb-3 mt-2 border-l-4 border-blue-400">
            <div className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-blue-600" />
              {titleText}
            </div>
            {remainingText && (
              <div className="text-gray-700 text-sm">
                {processLineText(remainingText, elementKey)}
              </div>
            )}
          </div>
        );
        return;
      } else if (propositionMatch3 && !trimmedLine.match(/^\d+\.\s/)) {
        // Format: **Titre** (mais pas si c'est une ligne numérotée déjà traitée)
        const titleText = propositionMatch3[1];
        const remainingText = propositionMatch3[2].trim();
        
        // Vérifier que ce n'est pas juste du texte en gras dans un paragraphe
        if (titleText.length < 100 && !remainingText.includes('**')) {
          elements.push(
            <div key={`proposition-${lineIndex}`} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 mb-3 mt-2 border-l-4 border-blue-400">
              <div className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-blue-600" />
                {titleText}
              </div>
              {remainingText && (
                <div className="text-gray-700 text-sm">
                  {processLineText(remainingText, elementKey)}
                </div>
              )}
            </div>
          );
          return;
        }
      }

      // Détecter les blocs d'information spéciaux
      const infoBlockMatch = trimmedLine.match(/^(INFO|NOTE|ASTUCE|ATTENTION):\s*(.+)$/i);
      if (infoBlockMatch) {
        const type = infoBlockMatch[1].toUpperCase();
        const text = infoBlockMatch[2];
        
        const blockStyles = {
          'INFO': { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', icon: Info },
          'NOTE': { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-800', icon: Info },
          'ASTUCE': { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', icon: Lightbulb },
          'ATTENTION': { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800', icon: AlertCircle }
        };
        
        const style = blockStyles[type] || blockStyles['INFO'];
        const IconComponent = style.icon;
        
        elements.push(
          <div key={`info-${lineIndex}`} className={`${style.bg} ${style.border} border rounded-lg p-3 mb-3 mt-2`}>
            <div className={`flex items-start gap-2 ${style.text}`}>
              <IconComponent className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-semibold text-xs uppercase tracking-wider">{type}</span>
                <div className="mt-1">
                  {processLineText(text, elementKey)}
                </div>
              </div>
            </div>
          </div>
        );
        return;
      }

      // Paragraphe normal
      const processedLine = processLineText(trimmedLine, elementKey);
      elementKey += 1000;
      
      elements.push(
        <p key={`p-${lineIndex}`} className="mb-2 text-gray-700 leading-relaxed">
          {processedLine}
        </p>
      );
    });

    return elements;
  };

  // Fonction pour traiter le texte d'une ligne (liens, gras, etc.)
  const processLineText = (text, startIndex = 0) => {
    if (!text) return [];
    
    const elements = [];
    let lastIndex = 0;

    // 1. D'abord traiter les liens markdown [texte](url)
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    let match;
    
    while ((match = linkRegex.exec(text)) !== null) {
      // Ajouter le texte avant le lien
      if (match.index > lastIndex) {
        const beforeText = text.slice(lastIndex, match.index);
        if (beforeText) {
          // ✅ FIX: Appeler processCitations() directement au lieu de processUrlsAndFormatting()
          elements.push(...processCitations(beforeText, startIndex + lastIndex));
        }
      }
      
      // Ajouter le lien markdown formaté
      const linkText = match[1];
      const linkUrl = match[2];
      
      // Vérifier que l'URL est valide
      const isValidUrl = /^https?:\/\/.+/.test(linkUrl);
      
      elements.push(
        <a
          key={`link-${startIndex + match.index}`}
          href={isValidUrl ? linkUrl : `https://${linkUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-600 rounded-md text-sm hover:bg-green-100 transition-colors font-medium border border-green-200 hover:border-green-300 no-underline hover:no-underline cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            console.log('🔗 Clic sur lien:', linkUrl);
          }}
        >
          <span className="max-w-xs truncate" title={linkText}>{linkText}</span>
          <ExternalLink size={12} className="flex-shrink-0" />
        </a>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Ajouter le texte restant
    if (lastIndex < text.length) {
      const remainingText = text.slice(lastIndex);
      if (remainingText) {
        // ✅ FIX: Appeler processCitations() directement au lieu de processUrlsAndFormatting()
        elements.push(...processCitations(remainingText, startIndex + lastIndex));
      }
    }
    
    // ✅ FIX: Appeler processCitations() directement au lieu de processUrlsAndFormatting()
    return elements.length > 0 ? elements : processCitations(text, startIndex);
  };

  // ✨ NOUVELLE FONCTION : Traiter les citations [x]
  const processCitations = (text, startIndex) => {
    if (!text || !citations || citations.length === 0) {
      return processBoldItalic(text, startIndex);
    }
    
    console.log('🔍 processCitations appelée avec:', { text: text.substring(0, 100), citationsCount: citations.length });
    
    const elements = [];
    let lastIndex = 0;
    
    // Regex pour détecter les citations [1], [2], etc.
    const citationRegex = /\[(\d+)\]/g;
    let citationMatch;
    
    while ((citationMatch = citationRegex.exec(text)) !== null) {
      // Texte avant la citation
      if (citationMatch.index > lastIndex) {
        const beforeText = text.slice(lastIndex, citationMatch.index);
        if (beforeText) {
          elements.push(...processBoldItalic(beforeText, startIndex + lastIndex));
        }
      }
      
      const citationNumber = parseInt(citationMatch[1]);
      const citation = citations[citationNumber - 1];
      
      if (citation) {
        // Citation cliquable
        elements.push(
          <button
            key={`citation-${startIndex}-${citationMatch.index}`}
            onClick={(e) => {
              e.stopPropagation();
              if (onCitationClick) {
                onCitationClick(citationNumber);
              }
              // ✨ FIX: Scroll vers la citation dans SourcesPanel avec messageId
              const citationId = messageId ? `citation-${messageId}-${citationNumber}` : `citation-${citationNumber}`;
              const citationElement = document.getElementById(citationId);
              if (citationElement) {
                citationElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                // Effet de surbrillance temporaire
                citationElement.classList.add('bg-yellow-100');
                setTimeout(() => {
                  citationElement.classList.remove('bg-yellow-100');
                }, 2000);
              } else {
                console.warn(`⚠️ Citation element not found: ${citationId}`);
              }
            }}
            className="inline-flex items-center px-1 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-mono hover:bg-blue-200 transition-colors cursor-pointer border border-blue-300 hover:border-blue-400"
            title={citation.title || citation.url || citation}
          >
            [{citationNumber}]
          </button>
        );
      } else {
        // Citation non trouvée, afficher en texte simple
        elements.push(
          <span key={`citation-${startIndex}-${citationMatch.index}`} className="font-mono text-gray-500">
            [{citationNumber}]
          </span>
        );
      }
      
      lastIndex = citationMatch.index + citationMatch[0].length;
    }
    
    // Texte restant
    if (lastIndex < text.length) {
      const remainingText = text.slice(lastIndex);
      if (remainingText) {
        elements.push(...processBoldItalic(remainingText, startIndex + lastIndex));
      }
    }
    
    return elements.length > 0 ? elements : processBoldItalic(text, startIndex);
  };

  // Nouvelle fonction pour traiter les URLs nues et le formatage
  const processUrlsAndFormatting = (text, startIndex) => {
    if (!text) return [];
    
    const elements = [];
    let lastIndex = 0;

    // Regex améliorée pour détecter les URLs complètes
    const urlRegex = /(https?:\/\/[^\s\)>\]]+(?:[^\s\.,;!?'")\]}>]|[.,;!?'")\]}>](?!\s|$))*)/g;
    let urlMatch;
    
    while ((urlMatch = urlRegex.exec(text)) !== null) {
      // Texte avant l'URL
      if (urlMatch.index > lastIndex) {
        const beforeText = text.slice(lastIndex, urlMatch.index);
        elements.push(...processCitations(beforeText, startIndex + lastIndex));
      }
      
      // URL cliquable
      const url = urlMatch[1];
      const cleanUrl = url.replace(/[.,;!?'")\]}>]+$/, '');
      const displayUrl = cleanUrl.length > 50 ? `${cleanUrl.substring(0, 47)}...` : cleanUrl;
      
      elements.push(
        <a
          key={`url-${startIndex + urlMatch.index}`}
          href={cleanUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded-md text-sm hover:bg-blue-100 transition-colors border border-blue-200 hover:border-blue-300 no-underline hover:no-underline cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            console.log('🔗 Clic sur URL:', cleanUrl);
          }}
        >
          <span className="max-w-xs truncate" title={cleanUrl}>{displayUrl}</span>
          <ExternalLink size={12} className="flex-shrink-0" />
        </a>
      );
      
      lastIndex = urlMatch.index + urlMatch[0].length;
    }
    
    // Texte restant
    if (lastIndex < text.length) {
      const remainingText = text.slice(lastIndex);
      if (remainingText) {
        elements.push(...processBoldItalic(remainingText, startIndex + lastIndex));
      }
    }
    
    return elements.length > 0 ? elements : processBoldItalic(text, startIndex);
  };

  // Fonction pour traiter gras et italique
  const processBoldItalic = (text, startIndex) => {
    if (!text) return [];
    
    const elements = [];
    let lastIndex = 0;

    // Traiter le gras **texte**
    const boldRegex = /\*\*(.*?)\*\*/g;
    let boldMatch;
    
    while ((boldMatch = boldRegex.exec(text)) !== null) {
      // Texte avant le gras
      if (boldMatch.index > lastIndex) {
        const beforeText = text.slice(lastIndex, boldMatch.index);
        if (beforeText) {
          elements.push(...processItalic(beforeText, startIndex + lastIndex));
        }
      }
      
      // Texte en gras
      elements.push(
        <strong 
          key={`bold-${startIndex + boldMatch.index}`}
          className="font-semibold text-gray-900"
        >
          {processItalic(boldMatch[1], startIndex + boldMatch.index + 2)}
        </strong>
      );
      
      lastIndex = boldMatch.index + boldMatch[0].length;
    }
    
    // Texte restant
    if (lastIndex < text.length) {
      const remainingText = text.slice(lastIndex);
      if (remainingText) {
        elements.push(...processItalic(remainingText, startIndex + lastIndex));
      }
    }
    
    return elements.length > 0 ? elements : processItalic(text, startIndex);
  };

  // Fonction pour traiter l'italique
  const processItalic = (text, startIndex) => {
    if (!text) return [];
    
    const elements = [];
    let lastIndex = 0;

    // Traiter l'italique *texte*
    const italicRegex = /(?<!\*)\*([^*]+)\*(?!\*)/g;
    let italicMatch;
    
    while ((italicMatch = italicRegex.exec(text)) !== null) {
      // Texte avant l'italique
      if (italicMatch.index > lastIndex) {
        const beforeText = text.slice(lastIndex, italicMatch.index);
        if (beforeText) {
          elements.push(
            <span key={`text-${startIndex + lastIndex}`}>
              {beforeText}
            </span>
          );
        }
      }
      
      // Texte en italique
      elements.push(
        <em 
          key={`italic-${startIndex + italicMatch.index}`}
          className="italic text-gray-600"
        >
          {italicMatch[1]}
        </em>
      );
      
      lastIndex = italicMatch.index + italicMatch[0].length;
    }
    
    // Texte restant
    if (lastIndex < text.length) {
      const remainingText = text.slice(lastIndex);
      if (remainingText) {
        elements.push(
          <span key={`text-${startIndex + lastIndex}`}>
            {remainingText}
          </span>
        );
      }
    }
    
    return elements.length > 0 ? elements : [
      <span key={`text-${startIndex}`}>{text}</span>
    ];
  };

  try {
    const parsedElements = parseContent(actualContent);

    return (
      <div className={`max-w-none ${className}`}>
        {parsedElements}
      </div>
    );
  } catch (error) {
    console.error('❌ Erreur dans EnhancedChatMarkdown:', error);
    return (
      <div className="text-red-600 bg-red-50 p-3 rounded">
        Erreur de formatage: {error.message}
      </div>
    );
  }
};

export default EnhancedChatMarkdown;
